import { useState } from "react";
import { Download, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { toast } from "sonner";

// ago-2026 · Un solo libro: el visor de pólizas ya recibe tanto las del ciclo de crédito
// (derivadas al vuelo) como las persistidas de Administración financiera (capital, fondeo,
// gastos). Estas etiquetas cubren AMBAS familias de tipos.
export const POLIZA_TIPO_LABEL = {
  disposicion: "Disposición",
  pago: "Pago",
  cancelacion: "Cancelación",
  gasto_cobranza_devengado: "Gasto de cobranza (devengo)",
  desembolso_arrendamiento: "Desembolso de arrendamiento",
  renta: "Renta",
  compra_bien: "Compra del bien",
  prorroga: "Prórroga",
  devolucion: "Devolución del bien",
  devolucion_cancelacion: "Cancelación de devolución",
  diario: "Diario",
  egresos: "Egresos",
  ingresos: "Ingresos",
};

export const POLIZA_TIPO_CLS = {
  disposicion: "bg-blue-100 text-blue-700",
  pago: "bg-emerald-100 text-emerald-700",
  cancelacion: "bg-red-100 text-red-700",
  gasto_cobranza_devengado: "bg-amber-100 text-amber-800",
  desembolso_arrendamiento: "bg-indigo-100 text-indigo-700",
  renta: "bg-emerald-100 text-emerald-800",
  compra_bien: "bg-cyan-100 text-cyan-800",
  prorroga: "bg-sky-100 text-sky-800",
  devolucion: "bg-stone-200 text-stone-800",
  devolucion_cancelacion: "bg-red-100 text-red-700",
  diario: "bg-violet-100 text-violet-700",
  egresos: "bg-orange-100 text-orange-700",
  ingresos: "bg-teal-100 text-teal-700",
};

export const POLIZA_SUBTIPO_LABEL = {
  aportacion_capital: "Aportación de capital",
  prestamo_recibido: "Fondeo recibido",
  pago_fondeo: "Pago de fondeo",
  gasto_operativo: "Gasto operativo",
};

export function periodoDe(pz, periodoFallback) {
  const f = pz?.fecha || "";
  return /^\d{4}-\d{2}/.test(f) ? f.slice(0, 7) : periodoFallback;
}

export async function descargarPolizaPdf(pz, periodoFallback) {
  const periodo = periodoDe(pz, periodoFallback);
  const res = await api.get("/fiscal/polizas/pdf", {
    params: { periodo, referencia: pz.referencia, tipo: pz.tipo },
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `poliza-${pz.tipo}-${pz.referencia}-${periodo}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function PolizaCard({ pz, periodo, compacta = false }) {
  const [bajando, setBajando] = useState(false);
  const ref = pz?.referencia || "";
  const bajar = async () => {
    if (bajando) return;
    setBajando(true);
    try {
      await descargarPolizaPdf(pz, periodo);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBajando(false);
    }
  };
  return (
    <div data-testid={`poliza-${ref}`}
         className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid={`poliza-tipo-${pz.tipo}-${ref}`}
                className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${POLIZA_TIPO_CLS[pz.tipo] || "bg-slate-100 text-slate-700"}`}>
            {POLIZA_TIPO_LABEL[pz.tipo] || pz.tipo}
          </span>
          {pz.origen === "finanzas" && (
            <Badge variant="outline" data-testid={`poliza-origen-finanzas-${ref}`}
                   className="border-violet-300 bg-violet-50 text-[11px] text-violet-800">
              {POLIZA_SUBTIPO_LABEL[pz.subtipo] || "Administración financiera"}
            </Badge>
          )}
          <span className="font-mono text-xs text-slate-600">{ref}</span>
          <span className="text-xs text-slate-400">{pz.fecha}</span>
        </div>
        <div className="flex items-center gap-2">
          {pz.balanceada
            ? <Badge data-testid="poliza-balanceada" className="gap-1 bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Balanceada</Badge>
            : <Badge className="gap-1 bg-red-100 text-red-700"><XCircle className="h-3 w-3" /> Descuadrada</Badge>}
          <Button data-testid={`descargar-poliza-pdf-${ref}`}
                  size="sm" variant="outline" className="h-7 px-2"
                  disabled={bajando} onClick={bajar}
                  title="Descargar PDF de esta póliza (documento informativo)">
            {bajando ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                     : <Download className="mr-1 h-3.5 w-3.5" />} PDF
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cuenta</TableHead>
            {!compacta && <TableHead>Agrupador</TableHead>}
            <TableHead>Nombre</TableHead>
            <TableHead className="text-right">Cargo</TableHead>
            <TableHead className="text-right">Abono</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(pz.movimientos || []).map((m, j) => (
            <TableRow key={j}>
              <TableCell className="font-mono text-xs">{m.codigo}</TableCell>
              {!compacta && <TableCell className="font-mono text-xs">{m.agrupador}</TableCell>}
              <TableCell className="text-xs">{m.nombre}</TableCell>
              <TableCell className="text-right tabular-nums">{m.cargo ? mxn(m.cargo) : "—"}</TableCell>
              <TableCell className="text-right tabular-nums">{m.abono ? mxn(m.abono) : "—"}</TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-slate-50 font-medium">
            <TableCell colSpan={compacta ? 2 : 3} className="text-right text-xs">Totales</TableCell>
            <TableCell className="text-right tabular-nums">{mxn(pz.total_cargos)}</TableCell>
            <TableCell className="text-right tabular-nums">{mxn(pz.total_abonos)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
